/**
 * 一番賞商品頁排隊資訊列（頁面上、不 sticky）的顯示狀態。
 *
 * 排隊狀態機不在這裡，一律吃 queue-state.mjs 的 queueViewState() 結論——
 * 資訊列與底部列共用同一份倒數，兩邊各自重算會漂移成兩個不同的秒數。
 */

/** 只有還握著隊列位置的人才需要離開排隊。 */
const HOLDING_A_PLACE_STATES = ['in_queue', 'summoned', 'active'];

/**
 * 叫號中佔用倒數欄位的固定文案。資訊列與底部列共用這一份，
 * 兩邊各寫一次字面字串遲早會漂移成兩種說法。
 */
export const JOINING_TEXT = '加入中…';

function num(value) {
    const n = Number(value);

    return Number.isFinite(n) ? n : 0;
}

/**
 * 倒數顯示字串，一律以秒計。沒有倒數（null）回空字串；已經歸零的倒數仍回「0 秒」，
 * 該不該把它顯示出來由呼叫端的 visible 判定決定。
 *
 * @param {number|null|undefined} seconds
 * @returns {string}
 */
export function formatCountdown(seconds) {
    if (seconds == null) {
        return '';
    }

    return `${Math.max(0, Math.floor(seconds))} 秒`;
}

const OWN_COUNTDOWN_LABEL = '你的抽獎時間';

/**
 * 倒數欄位。叫號中那段沒有秒數可報，改佔著同一格說「加入中…」——欄位留在原位，
 * 抽獎時間接上來時不會有一塊區域憑空長出來。不進警示配色。
 *
 * 標題沿用「你的抽獎時間」：接下來變成秒數的就是同一段時間，換成別的字或清空，
 * 只會讓這一格在轉成 active 的瞬間左右重排。
 *
 * trackVisible 管的是進度條的軌道本身，不是填色寬度。叫號中沒有進度可畫，
 * 只把 percent 設成 null 的話軌道那條灰底仍留在畫面上，看起來像一條永遠不動的進度條。
 *
 * @param {object} view queueViewState() 的結論
 */
function timerOf(view) {
    if (view.isJoining) {
        return {
            visible: true,
            trackVisible: false,
            owner: null,
            label: OWN_COUNTDOWN_LABEL,
            seconds: null,
            text: JOINING_TEXT,
            percent: null,
            warn: false,
        };
    }

    return {
        visible: view.countdownOwner !== null,
        trackVisible: view.countdownOwner !== null,
        owner: view.countdownOwner,
        label: view.countdownOwner === 'self' ? OWN_COUNTDOWN_LABEL : '本輪剩餘',
        seconds: view.remainingSeconds,
        text: formatCountdown(view.remainingSeconds),
        percent: view.progressPercent,
        warn: view.isWarning,
    };
}

/**
 * 資訊列的兩項統計、倒數與離開排隊。前方人數歸底部列，那裡才是講「我現在怎麼樣」的地方。
 * 免排隊商品整段不輸出，回 null。
 *
 * @param {object|null|undefined} view queueViewState() 的結論
 * @param {number|string} remaining 剩餘抽數
 * @param {number|string} total 總抽數
 * @returns {{
 *   stats: Array<{label: string, value: string}>,
 *   timer: {visible: boolean, trackVisible: boolean, owner: ('self'|'round'|null), label: string, seconds: (number|null), text: string, percent: (number|null), warn: boolean},
 *   leaveVisible: boolean
 * }|null}
 */
export function infoBarState(view, remaining, total) {
    if (!view || !view.hasQueue) {
        return null;
    }

    return {
        stats: [
            { label: '抽獎籤別', value: `${num(remaining)} / ${num(total)} 抽` },
            { label: '目前等待人數', value: `${num(view.waitingCount)} 人` },
        ],
        timer: timerOf(view),
        leaveVisible: HOLDING_A_PLACE_STATES.includes(view.state),
    };
}
